import React, { useEffect, useMemo, useState } from "react";
import JobsHero from "../HeroSection";
import jobsData from "../_jobs.json";

import "./Jobs.scss";

const getHashText = () => {
  if (typeof window === "undefined") {
    return "";
  }

  return decodeURIComponent(window.location.hash.replace(/^#/, "")).trim();
};

const setHashFromTitle = (title) => {
  if (typeof window === "undefined") {
    return;
  }

  const pathWithQuery = `${window.location.pathname}${window.location.search}`;
  const nextHash = title ? `#${encodeURIComponent(title)}` : "";

  if (window.location.hash !== nextHash) {
    window.history.replaceState(null, "", `${pathWithQuery}${nextHash}`);
  }
};

const formatLocationBadge = (location = "") => {
  const value = location.trim();
  if (!value.includes(",")) {
    return value;
  }

  const parts = value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 2) {
    return value;
  }

  return parts[parts.length - 1]?.trim() || location;
};

const Jobs = () => {
  const [query, setQuery] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("ALL");
  const [hasProcessedInitialHash, setHasProcessedInitialHash] = useState(false);

  const allJobs = useMemo(
    () =>
      jobsData.flatMap((company) =>
        company.jobOpenings.map((job, index) => ({
          ...job,
          companyCode: company.companyCode,
          companyName: company.companyName,
          id: `${company.companyCode}-${index}`,
        })),
      ),
    [],
  );

  const companyOptions = useMemo(
    () =>
      jobsData.map((company) => ({
        code: company.companyCode,
        name: company.companyName,
      })),
    [],
  );

  const uniqueLocationCount = useMemo(() => {
    const normalizedLocations = allJobs
      .map((job) => (job.location || "").trim().toLowerCase())
      .filter(Boolean);

    return new Set(normalizedLocations).size;
  }, [allJobs]);

  const filteredJobs = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);

    const rankedMatches = [];

    allJobs.forEach((job, index) => {
      const companyMatch =
        selectedCompany === "ALL" || job.companyCode === selectedCompany;

      if (!companyMatch) {
        return;
      }

      if (!terms.length) {
        rankedMatches.push({ job, score: 0, index });
        return;
      }

      const positionTitle = (job.positionTitle || "").toLowerCase();
      const searchableText = [
        positionTitle,
        job.companyName,
        job.location,
        job.employmentType,
        job.expectedStartDate,
        ...(job.jobDescription || []),
        ...(job.qualifications || []),
      ]
        .join(" ")
        .toLowerCase();

      const matchesAllTerms = terms.every((term) =>
        searchableText.includes(term),
      );
      if (!matchesAllTerms) {
        return;
      }

      const titleMatchCount = terms.reduce(
        (count, term) => (positionTitle.includes(term) ? count + 1 : count),
        0,
      );

      rankedMatches.push({
        job,
        score: titleMatchCount,
        index,
      });
    });

    rankedMatches.sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }

      return a.index - b.index;
    });

    return rankedMatches.map((entry) => entry.job);
  }, [allJobs, query, selectedCompany]);

  const [openJobId, setOpenJobId] = useState(null);

  useEffect(() => {
    if (!filteredJobs.length) {
      setOpenJobId(null);
      return;
    }

    if (openJobId === null) {
      return;
    }

    const isOpenJobVisible = filteredJobs.some((job) => job.id === openJobId);

    if (!isOpenJobVisible) {
      setOpenJobId(filteredJobs[0].id);
    }
  }, [filteredJobs, openJobId]);

  useEffect(() => {
    const hashText = getHashText();

    if (!hashText) {
      setHasProcessedInitialHash(true);
      return;
    }

    setQuery(hashText);
    setSelectedCompany("ALL");

    const normalizedHash = hashText.toLowerCase();
    const exactMatch = allJobs.find(
      (job) => job.positionTitle.toLowerCase() === normalizedHash,
    );
    const partialMatch = allJobs.find((job) =>
      job.positionTitle.toLowerCase().includes(normalizedHash),
    );
    const matchedJob = exactMatch || partialMatch;

    if (matchedJob) {
      setOpenJobId(matchedJob.id);
      setHashFromTitle(matchedJob.positionTitle);
    } else {
      setOpenJobId(null);
      setHashFromTitle("");
    }

    setHasProcessedInitialHash(true);
  }, [allJobs]);

  useEffect(() => {
    if (!hasProcessedInitialHash) {
      return;
    }

    if (openJobId === null) {
      setHashFromTitle("");
      return;
    }

    const openedJob = allJobs.find((job) => job.id === openJobId);
    setHashFromTitle(openedJob ? openedJob.positionTitle : "");
  }, [allJobs, hasProcessedInitialHash, openJobId]);

  const handleJobToggle = (job, isOpen) => {
    if (isOpen) {
      setOpenJobId(null);
      return;
    }

    setOpenJobId(job.id);
  };

  return (
    <>
      <JobsHero
        totalJobs={allJobs.length}
        uniqueLocationCount={uniqueLocationCount}
      />
      <section className="careers-board">
        <div className="careers-intro">
          <p>
            Explore openings across the STAR group. Use search and filters to
            find the role that matches your experience.
          </p>
        </div>
        <div className="careers-controls">
          <div>
            <label htmlFor="job-search" className="control-label">
              Search for a role, location, skill, or keyword
            </label>
            <input
              id="job-search"
              type="search"
              placeholder="Example: procurement, engineer, Batangas"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="company-filter" className="control-label">
              Filter by company
            </label>
            <select
              id="company-filter"
              value={selectedCompany}
              onChange={(event) => setSelectedCompany(event.target.value)}
            >
              <option value="ALL">All Companies</option>
              {companyOptions.map((company) => (
                <option key={company.code} value={company.code}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="careers-results">
          Showing {filteredJobs.length} of {allJobs.length} Open Positions
        </p>
        <div className="jobs-list" role="list">
          {filteredJobs.map((job) => {
            const isOpen = openJobId === job.id;

            return (
              <article key={job.id} className="job-card" role="listitem">
                <button
                  type="button"
                  className="job-card-header"
                  onClick={() => handleJobToggle(job, isOpen)}
                  aria-expanded={isOpen}
                  aria-controls={`job-panel-${job.id}`}
                >
                  <span className="title-wrap">
                    <strong> {job.positionTitle}</strong>
                    <small> {job.companyName}</small>
                  </span>
                  <span className="meta-wrap">
                    {!isOpen && (
                      <span className="location-badge">
                        {formatLocationBadge(job.location)}
                      </span>
                    )}
                    <span> {job.employmentType}</span>
                    <span className="chevron" aria-hidden="true">
                      {isOpen ? "−" : "+"}
                    </span>
                  </span>
                </button>
                <div
                  className={`job-card-panel-wrap ${
                    isOpen ? "open" : "closed"
                  }`}
                  aria-hidden={!isOpen}
                >
                  <div className="job-card-panel" id={`job-panel-${job.id}`}>
                    <div className="job-meta">
                      <p>
                        Apply now as a {job.positionTitle} at {job.companyName}
                        <hr />
                        <strong>Location:</strong> {job.location} <br />
                        <strong>Expected start:</strong> {job.expectedStartDate}
                      </p>
                    </div>
                    <hr />
                    <div className="job-columns">
                      <div>
                        <h3>Job Description</h3>
                        <ul>
                          {(job.jobDescription || []).map((item, idx) => (
                            <li key={`job-${job.id}-desc-${idx}`}>{item}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h3>Qualifications</h3>
                        <ul>
                          {(job.qualifications || []).map((item, idx) => (
                            <li key={`job-${job.id}-qual-${idx}`}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
          {!filteredJobs.length && (
            <div className="jobs-empty-state">
              <p>No matching positions found.</p>
              <p>Try another keyword or reset the company filter.</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Jobs;
